import { useEffect, useRef, useState } from "react";
import { ArrowUp } from "lucide-react";

export const scrollToEl = (el, offset = -120) => {
  if (!el) return;
  if (window.__lenis) window.__lenis.scrollTo(el, { offset, duration: 1 });
  else window.scrollTo({ top: el.getBoundingClientRect().top + window.scrollY + offset, behavior: "smooth" });
};

export function SectionNav({ items, idFor, label = "Chapter", testId = "chapter-nav" }) {
  const [active, setActive] = useState(items[0]?.key);
  const stripRef = useRef(null);

  useEffect(() => {
    const obs = new IntersectionObserver(
      (entries) => {
        const hit = entries.filter((e) => e.isIntersecting).sort((a, b) => a.boundingClientRect.top - b.boundingClientRect.top)[0];
        if (hit) setActive(hit.target.dataset.section);
      },
      { rootMargin: "-140px 0px -60% 0px", threshold: 0 }
    );
    items.forEach((it) => {
      const el = document.getElementById(idFor(it.key));
      if (el) obs.observe(el);
    });
    return () => obs.disconnect();
  }, [items, idFor]);

  useEffect(() => {
    const strip = stripRef.current;
    const chip = strip?.querySelector(`[data-section="${CSS.escape(String(active))}"]`);
    if (strip && chip) strip.scrollTo({ left: chip.offsetLeft - strip.clientWidth / 2 + chip.clientWidth / 2, behavior: "smooth" });
  }, [active]);

  if (items.length < 2) return null;
  return (
    <nav
      className="sticky top-16 z-30 -mx-4 mb-10 border-b border-border bg-background/85 px-4 py-3 glass sm:mx-0 sm:rounded-2xl sm:border sm:px-4"
      aria-label={`Jump to ${label.toLowerCase()}`}
      data-testid={testId}
    >
      <div className="flex items-center gap-3">
        <span className="shrink-0 font-mono text-[10px] uppercase tracking-[0.2em] text-muted-foreground">{label}</span>
        <div ref={stripRef} className="flex flex-1 gap-1.5 overflow-x-auto pb-1 [scrollbar-width:thin]" data-lenis-prevent>
          {items.map((it) => (
            <button
              key={it.key}
              type="button"
              data-section={it.key}
              onClick={() => scrollToEl(document.getElementById(idFor(it.key)))}
              className={`shrink-0 rounded-full px-3 py-1 font-mono text-xs transition-colors ${
                String(active) === String(it.key) ? "bg-primary text-primary-foreground" : "bg-card text-muted-foreground hover:bg-muted hover:text-foreground"
              }`}
              title={`${label} ${it.label} · ${it.count} question${it.count === 1 ? "" : "s"}`}
              data-testid={`${testId}-jump-${String(it.key).toLowerCase().replace(/\s+/g, "-")}`}
            >
              {it.label}
            </button>
          ))}
        </div>
      </div>
    </nav>
  );
}

export function ChapterNav({ chapters, idFor }) {
  const items = chapters.map((ch) => ({ key: ch.chapter, label: ch.chapter, count: ch.questions.length }));
  return <SectionNav items={items} idFor={idFor} label="Chapter" />;
}

export function BackToTop() {
  const [show, setShow] = useState(false);
  useEffect(() => {
    const onScroll = () => setShow(window.scrollY > 900);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);
  return (
    <button
      type="button"
      onClick={() => (window.__lenis ? window.__lenis.scrollTo(0, { duration: 1 }) : window.scrollTo({ top: 0, behavior: "smooth" }))}
      aria-label="Back to top"
      data-testid="back-to-top"
      className={`fixed bottom-6 right-6 z-40 flex h-11 w-11 items-center justify-center rounded-full bg-primary text-primary-foreground shadow-lg transition-all duration-300 hover:-translate-y-0.5 hover:bg-primary/90 ${
        show ? "translate-y-0 opacity-100" : "pointer-events-none translate-y-4 opacity-0"
      }`}
    >
      <ArrowUp className="h-5 w-5" />
    </button>
  );
}
